import React, { useMemo } from 'react';
import { Form, Modal, Button, Schema } from 'rsuite';
import { Field, CellPhoneField } from '../../../../helpers/rsuite/Form';
import { cuotitasStoreService } from '../../../../services';
import { alert } from '../../../../helpers/rsuite/Alerts';
import { errorMapper } from '../../../../helpers/ErrorMappers';
import { asyncService, addStorage } from '../../../../helpers';
import { STEPS } from '../../../../constants/loanevents/steps';
import ENABLE from '../../../../constants/enable';
import { logCustomEvent } from '../../../../../config/rum';
import { EVENTS } from '../../../../constants/rumevents/events';

const body0 = {
    width: '540px',
    padding: '0 4px'
};

const required = 'Este campo es requerido.';
const msgNumber = 'Por favor, ingrese un número valido.';
const { StringType, NumberType } = Schema.Types;
const INVALID_VALUES = ['null', 'undefined', '', null, undefined];

const modelCheckUser = Schema.Model({
    firstName: StringType().isRequired(required),
    lastName: StringType().isRequired(required),
    cellPhoneAreaCode: NumberType(msgNumber)
        .addRule((value, data) => {
            if (value?.length > 4) {
                return false;
            }
            return true;
        }, 'Por favor, ingrese un código de teléfono valido')
        .isRequired(required),
    cellPhoneCharacteristic: NumberType(msgNumber)
        .addRule((value, data) => {
            if (value?.length > 4) {
                return false;
            }
            return true;
        }, 'Por favor, ingrese una característica de teléfono valida')
        .isRequired(required),
    cellPhoneNumber: NumberType(msgNumber)
        .addRule((value, data) => {
            if (value?.length !== 4) {
                return false;
            }
            return true;
        }, 'Por favor, ingrese un número de teléfono valido')
        .isRequired(required),
});

function CreatePersonStep (context) {
    // state

    const formMainUser = context.formMainUser;
    const formCheckUser = context.formCheckUser;
    const formRef = context.formRef;

    const isLoading = context.isLoading;
    const step = context.step;

    const id = formMainUser.id || undefined;
    const bornDate = !INVALID_VALUES.includes(formMainUser.bornDate) ? formMainUser.bornDate : '';
    const firstName = formMainUser.firstName;
    const lastName = formMainUser.lastName;
    const gender = formCheckUser.gender;
    const document = formCheckUser.document.toString();
    const cellPhoneAreaCode = formCheckUser.cellPhoneAreaCode;
    const cellPhoneCharacteristic = formCheckUser.cellPhoneCharacteristic;
    const cellPhoneNumber = formCheckUser.cellPhoneNumber;

    const telephoneResponse = formMainUser.telephone;

    const setFormCheckUser = context.setFormCheckUser;
    const setFormErrorCheckUser = context.setFormErrorCheckUser;
    const setIsLoading = context.setIsLoading;
    const setPoliticies = context.setPoliticies;
    const setInstallmentsExtraData = context.setInstallmentsExtraData;
    const setOffer = context.setOffer;
    const setStep = context.setStep;
    const populateUser = context.populateUser;
    const nextStep = context.nextStep;
    const submitToast = context.submitToast;
    const onHide = context.onHide;

    const idStore = context.idStore;
    const formInstallments = context.formInstallments;
    const setFormInstallments = context.setFormInstallments;
    const setInstallment = context.setInstallment;
    const idLoanOffer = context.idLoanOffer;
    const setAmountLimit = context.setAmountLimit;
    const customStep = context.customStep;
    const fullName = context.formFullNameUser || 'Sin información';

    // Computed properties

    const isNewUser = useMemo(() => {
        return (firstName.length === 0 && lastName.length === 0);
    }, [
        firstName,
        lastName
    ]);

    const telephone = useMemo(() => {
        const area = cellPhoneAreaCode * 1;
        const characteristic = cellPhoneCharacteristic;
        const number = cellPhoneNumber;
        return `${area}${characteristic}${number}`;
    }, [
        cellPhoneAreaCode,
        cellPhoneCharacteristic,
        cellPhoneNumber
    ]);

    const isTelephoneModified = useMemo(() => {
        const responseTelephone = (telephoneResponse * 1)?.toString();
        return responseTelephone !== telephone;
    }, [
        telephoneResponse,
        telephone,
    ]);

    // actions

    async function submit () {
        if (telephone.length !== 10) {
            return alert('error', 'Número de telefono celular invalido, este debe tener diez dígitos.');
        }

        if (!formRef.current.check()) {
            return console.error('Form Error');
        }

        setIsLoading(true);

        let userId = id;
        let politicies = [];

        try {
            // if user is new or telephone is modified => modify user
            if (isNewUser || isTelephoneModified) {
                [userId, politicies] = await Promise.all([
                    updateUser(),
                    findPoliticies(),
                ]);
            }
            else {
                politicies = await findPoliticies();
            }

            // quizas no es necesario usar este state
            setPoliticies(politicies);

            // para simular una politica solo compras => politicies.pop();
            // ----
            logCustomEvent(EVENTS.CREATE_PERSON_STEP, { politicies: politicies, userId: userId });
            if (politicies.length === 1) {
                const offer = await createOfferByPoliticie(politicies, userId);
                return setOffer(offer);
            }

            return nextStep(2);
        }
        catch (error) {
            console.error(error);
            if (step !== 99) { // TODO Unicamente si no existe la persona
                setStep(99);
            }
            if (step !== 1 || step === 99) {
                const msg = errorMapper(error);
                error.msg = msg;
                // Error genérico
                alert(msg?.type, msg?.message, 0, 'topCenter', () => submitToast(error));
            }
        }
        finally {
            setIsLoading(false);
        }
    };

    async function findPoliticies () {
        let politicies;
        politicies = await await cuotitasStoreService.getPoliticies();
        politicies = politicies.map(politic => ({
            id: politic.id,
            label: politic?.description?.replaceAll('_', ' '),
            // value is converted to string because it can't be an object, after it will be used as trade attribute
            value: JSON.stringify({
                idCommercePolicy: politic.id,
                idArticle: politic?.articles[0]?.id,
                policyCode: politic.code,
            })
        }));
        return politicies;
    }

    async function updateUser () {
        const response = await asyncService((qss) => cuotitasStoreService.createOrModifyUser({
            ...formCheckUser,
            id,
            telephone,
            document,
            gender,
            bornDate,
            step: isNewUser ? STEPS.PERSON_CREATION : STEPS.PERSON_MODIFICATION
        }, qss));
        populateUser(response.loanPerson);
        return response.loanPerson.id;
    }

    async function createOfferByPoliticie (politicie, personId) {
        const trade = JSON.parse(politicie[0].value);
        const localContext = {
            idStore,
            data: {
                id: personId, // TODO idPerson, del getPerson
                idArticle: trade.idArticle + '',
                idCommercePolicy: trade.idCommercePolicy + '',
            },
            errorOfferLoop: null,
            offer: null,
        };

        try {
            const response = await asyncService((qss) => cuotitasStoreService.createOffer(localContext.idStore, localContext.data, qss));

            // Added extra data for cloudwatch
            const headerExtra = {
                idLoanOfferLocal: response?.id,
                IdSolicitudPendiente: response?.id_external
            };

            addStorage('headerExtra', headerExtra);

            localContext.offer = response;

            setOffer(localContext.offer);

            const installments = await cuotitasStoreService.getInstallmentsByOffer(idStore, localContext?.offer?.id);

            const installmentsList = installments?.installments;
            const amount = formInstallments.amount;

            setFormInstallments({
                ...formInstallments,
                id_loan_offer: installments.id || localContext?.offer?.id,
            });

            setInstallment(installmentsList?.map(r => {
                let cuota = 0;

                if (amount !== 0) {
                    cuota = (amount / r.installment) * r.totalCoefficient;
                }

                const matchedPlan = installments?.installmentsList?.find(
                    (i) => i.commercialPlanId === r.idCommercialPlan
                );
                const planCode = r.planCode || matchedPlan?.planCode || null;
                return {
                    installments: r.installments,
                    totalCoefficient: r.totalCoefficient,
                    label: r.installments + 'Cuotas de $' + cuota,
                    id_loan_offer: idLoanOffer || installments.id, // TODO mejorar
                    value: ENABLE?.enableInstallmentsWithDate ? JSON.stringify({ ...r, planCode }) : r.installments,
                    firstExpirationDate: r.firstExpirationDate,
                };
            }));

            setInstallmentsExtraData({
                idsPlan: installments.idsPlan || [],
                assistanceMapping: installments.assistanceMapping || [],
                commercialPlans: installments.commercialPlans || []
            });

            setAmountLimit({
                amountMinByPerson: installments?.min_amount,
                amountMaxByPerson: installments?.max_amount,
                amountMinOfInstallments: installments?.installmentsInfo?.amountMin,
                amountMaxOfInstallments: installments?.installmentsInfo?.amountMax,
                installmentAmountMax: installments?.metadata?.max_installment_amount
            });

            customStep(3, 50);
        }
        catch (error) {
            console.error('Error: ', error);
            const msg = errorMapper(error);
            msg.message = `${msg.message}. Cerrar para continuar.`.replace('..', '.').replace('. .', '.');
            alert(msg?.type, msg?.message, 0, 'topCenter', () => submitToast(error));
        }
        finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <Modal.Body className='mb-5 h-100 py-1' style={{ height: '540px' }}>
                <div className='row m-0' style={body0}>
                    <Form
                        fluid
                        ref={formRef}
                        formValue={formCheckUser}
                        onChange={setFormCheckUser}
                        onCheck={setFormErrorCheckUser}
                        model={modelCheckUser}
                    >
                        {isNewUser && (
                            <div className="mb-3 font-weight-bold">
                                <strong>Persona registrada:</strong> { fullName }
                            </div>
                        )}
                        <Field classNameGroup='mb-3' classNameLabel='font-weight-bold' name="firstName" label="Nombre" disabled={!isNewUser}/>
                        <Field classNameGroup='mb-3' classNameLabel='font-weight-bold' name="lastName" label="Apellido" disabled={!isNewUser}/>
                        <CellPhoneField
                            classNameGroup='mb-0'
                            classNameLabel='font-weight-bold'
                            name="cellPhone"
                            ref={formRef}
                            cellPhone={{
                                code: cellPhoneAreaCode * 1 || '',
                                characteristic: cellPhoneCharacteristic,
                                number: cellPhoneNumber
                            }}
                            label="Teléfono Celular"
                        />
                    </Form>
                </div>
            </Modal.Body>
            <Modal.Footer className='mt-4'>
                <div>
                    <Button appearance='subtle' id='cancel_btn' disabled={isLoading} onClick={onHide} >Cancelar</Button>
                    <Button appearance='primary' id='second_forward' disabled={isLoading} onClick={submit} >Siguiente</Button>
                </div>
            </Modal.Footer>
        </>
    );
};

export default CreatePersonStep;
